import { useCallback, useEffect, useState } from 'react';

interface BroadcastChannelHook {
  message: unknown;
  error: Event | null;
  isReady: boolean;
  sendMessage: (message: unknown) => void;
  resetMessage: () => void;
}

const useBroadcastChannel = (channelName: string): BroadcastChannelHook => {
  const [message, setMessage] = useState<unknown>(null);
  const [error, setError] = useState<Event | null>(null); // Update error type to Event
  const [channel, setChannel] = useState<BroadcastChannel | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let newChannel: BroadcastChannel | null = null;
    try {
      newChannel = new BroadcastChannel(channelName);

      newChannel.onmessage = (event) => {
        setMessage(event.data);
      };

      // Handle errors in onmessageerror directly
      newChannel.onmessageerror = (event) => {
        setError(event);
      };

      // Set channel and mark as ready
      setChannel(newChannel);
      setIsReady(true);
    } catch (err) {
      setError(err);
      setIsReady(false);
    }

    return () => {
      if (newChannel) {
        newChannel.close();
      }
      setIsReady(false);
    };
  }, [channelName]);

  // Stable across renders so consumers can safely list `sendMessage` in effect
  // dependency arrays (ENG-3395: an unstable identity here re-armed the
  // session-expiry timer every time the provider re-rendered).
  const sendMessage = useCallback(
    (message: unknown): void => {
      if (channel && isReady) {
        try {
          channel.postMessage(message);
        } catch (error) {
          setError(error as Event);
        }
      }
    },
    [channel, isReady]
  );

  const resetMessage = useCallback((): void => {
    setMessage(null);
  }, []);

  return { message, error, isReady, sendMessage, resetMessage };
};

export default useBroadcastChannel;
